import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useNavigate } from '@tanstack/react-router';
import { generateOrgColor, getInitials } from '@ui/primitives';
import { Button } from '@ui/primitives/button';
import { Input } from '@ui/primitives/input';
import { Separator } from '@ui/primitives/separator';
import { PlusCircleIcon, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCreateOrgModal } from '../create-org-modal';
export function OrgSwitcherOptions(param) {
    let { organizations, onOrgChange, onClose } = param;
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(null);
    const searchInputRef = useRef(null);
    const { openCreateOrgModal } = useCreateOrgModal();
    const { filteredOrgs } = useMemo(()=>{
        const orgsToFilter = !searchQuery ? organizations : organizations.filter((org)=>org.displayName.toLowerCase().includes(searchQuery.toLowerCase()) || org.name.toLowerCase().includes(searchQuery.toLowerCase()));
        const filteredOrgsRes = orgsToFilter.sort((a, b)=>a.displayName.localeCompare(b.displayName));
        return {
            filteredOrgs: filteredOrgsRes
        };
    }, [
        organizations,
        searchQuery
    ]);
    useEffect(()=>{
        setSelectedIndex(null);
    }, [
        filteredOrgs
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col flex-1 max-h-[90vh]",
        "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_OPTIONS_PANEL",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 p-3 sticky top-0 z-10 min-w-64 h-20",
                "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_SEARCH_BAR",
                children: [
                    /*#__PURE__*/ _jsx(Search, {
                        className: "h-4 w-4 text-muted-foreground"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        ref: searchInputRef,
                        placeholder: "Search organizations...",
                        "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_SEARCH_INPUT",
                        className: "text-sm text-gray-25 w-full h-8 focus:ring-2 focus:ring-primary border-0 !bg-transparent",
                        value: searchQuery,
                        onChange: (e)=>{
                            setSearchQuery(e.target.value);
                        },
                        onKeyDown: (e)=>{
                            const maxIndex = filteredOrgs.length - 1;
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setSelectedIndex((prevIndex)=>prevIndex === null ? 0 : prevIndex < maxIndex ? prevIndex + 1 : 0);
                            } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setSelectedIndex((prevIndex)=>prevIndex === null ? maxIndex : prevIndex > 0 ? prevIndex - 1 : maxIndex);
                            } else if (e.key === 'Enter' && selectedIndex !== null && filteredOrgs[selectedIndex]) {
                                e.preventDefault();
                                const selectedOrg = filteredOrgs[selectedIndex];
                                onOrgChange(selectedOrg.name);
                                onClose === null || onClose === void 0 ? void 0 : onClose();
                                navigate({
                                    to: "/org/".concat(selectedOrg.name)
                                }).catch((error)=>{
                                    if (error instanceof Error) {
                                        throw new Error("Navigation error: ".concat(error.message));
                                    }
                                    throw new Error('Unknown navigation error');
                                });
                            } else if (e.key === 'Escape') {
                                onClose === null || onClose === void 0 ? void 0 : onClose();
                            } else {
                                e.stopPropagation();
                            }
                        },
                        onClick: (e)=>{
                            e.stopPropagation();
                        },
                        autoFocus: true
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Separator, {}),
            /*#__PURE__*/ _jsx("ul", {
                className: "flex-1 min-h-17 p-2 overflow-y-auto list-none",
                "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_OPTIONS_LIST",
                children: filteredOrgs.length > 0 ? filteredOrgs.map((org, index)=>/*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            tabIndex: -1,
                            "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_OPTIONS_ITEM",
                            "data-org-name": org.name,
                            className: "flex items-center gap-2 p-2 rounded-sm cursor-pointer w-full text-left text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground transition-colors ".concat(index === selectedIndex ? 'bg-accent text-accent-foreground' : ''),
                            onClick: ()=>{
                                onOrgChange(org.name);
                                onClose === null || onClose === void 0 ? void 0 : onClose();
                                navigate({
                                    to: "/org/".concat(org.name)
                                }).catch((error)=>{
                                    if (error instanceof Error) {
                                        throw new Error("Navigation error: ".concat(error.message));
                                    }
                                    throw new Error('Unknown navigation error');
                                });
                            },
                            onMouseDown: (e)=>{
                                e.preventDefault();
                            },
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex size-8 items-center justify-center rounded-sm text-white shrink-0",
                                    style: {
                                        backgroundColor: generateOrgColor(org.name)
                                    },
                                    children: /*#__PURE__*/ _jsx("span", {
                                        className: "text-xs font-medium",
                                        children: getInitials(org.displayName)
                                    })
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: /*#__PURE__*/ _jsx("div", {
                                        className: "font-medium text-gray-25",
                                        children: org.displayName
                                    })
                                })
                            ]
                        })
                    }, org.id)) : /*#__PURE__*/ _jsx("div", {
                    className: "p-2 text-sm text-muted-foreground text-center",
                    "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_NO_RESULTS",
                    children: "No organizations found"
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "py-4 px-3.5 z-10 gap-2 flex flex-col items-center bottom-0",
                children: /*#__PURE__*/ _jsxs(Button, {
                    type: "button",
                    variant: "outline",
                    "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_CREATE_BUTTON",
                    className: "w-full text-foreground z-50 cursor-pointer",
                    onClick: ()=>{
                        onClose === null || onClose === void 0 ? void 0 : onClose();
                        openCreateOrgModal();
                    },
                    children: [
                        "Create new ",
                        /*#__PURE__*/ _jsx(PlusCircleIcon, {}),
                        ' '
                    ]
                })
            })
        ]
    });
}
